// Stale-chunk auto-recovery — PURE decision logic (no window/DOM/sessionStorage
// side effects here, so it is fully unit-testable). The impure wrapper that
// reads sessionStorage, records telemetry, and calls location.reload() lives in
// src/utils/staleChunk.ts and delegates the "reload or surface?" call here.

export const STALE_CHUNK_RELOAD_MARKER = 'stale-chunk-reload-guard-v3'
export const MAX_RELOAD_ATTEMPTS = 3
export const RELOAD_WINDOW_MS = 30_000

const STALE_CHUNK_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to load module script|dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i

export function isStaleChunkError(msg: unknown): boolean {
  return typeof msg === 'string' && STALE_CHUNK_RE.test(msg)
}

// During an alias flip the running bundle version flip-flops across the tear,
// so a version-keyed budget can be spent by a reload that lands back on the
// same un-propagated deployment; an href-keyed wall-clock budget rides through
// the flip.
export function staleChunkReloadKey(href: string): string {
  return `har_stale_chunk_reload:${href || ''}`
}

export type ReloadBurstState = { n: number; t0: number }

export type StaleChunkDecision = {
  isStaleChunk: boolean
  shouldReload: boolean
  key: string
  nextState: ReloadBurstState | null
}

export function decideStaleChunkReload(params: {
  message?: unknown
  assumeStale?: boolean
  href: string
  now: number
  state: ReloadBurstState | null
}): StaleChunkDecision {
  const key = staleChunkReloadKey(params.href)
  const isStaleChunk = params.assumeStale === true || isStaleChunkError(params.message)
  if (!isStaleChunk) return { isStaleChunk: false, shouldReload: false, key, nextState: params.state }
  const prior = params.state
  const windowActive = prior != null && (params.now - prior.t0) < RELOAD_WINDOW_MS
  if (!windowActive) return { isStaleChunk: true, shouldReload: true, key, nextState: { n: 1, t0: params.now } }
  if (prior.n < MAX_RELOAD_ATTEMPTS) return { isStaleChunk: true, shouldReload: true, key, nextState: { n: prior.n + 1, t0: prior.t0 } }
  return { isStaleChunk: true, shouldReload: false, key, nextState: prior }
}
